import { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import DashboardHeader from '../components/DashboardHeader';
import PolicyList from './components/PolicyList';
import PolicyViewer from './components/PolicyViewer';
import AddPolicyModal from './components/AddPolicyModal';
import EditPolicyModal from './components/EditPolicyModal';
import PolicyDetailModal from './components/PolicyDetailModal';

export interface Policy {
  id: string;
  title: string;
  category: string;
  description: string;
  content: string;
  effectiveDate: string;
  lastUpdated: string;
  updatedBy: string;
  version: string;
  status: 'Active' | 'Draft' | 'Archived';
  tags: string[];
}

export default function Policies() {
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [selectedPolicy, setSelectedPolicy] = useState<Policy | null>(null);
  const [viewingPolicy, setViewingPolicy] = useState<Policy | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<Policy | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedStatus, setSelectedStatus] = useState('All');

  const categories = ['All', 'HR', 'Safety', 'Operations', 'Finance', 'IT', 'Legal', 'Quality', 'Environmental'];

  useEffect(() => {
    const savedPolicies = localStorage.getItem('sparsh_policies');
    if (savedPolicies) {
      setPolicies(JSON.parse(savedPolicies));
    } else {
      const initialPolicies: Policy[] = [
        {
          id: '1',
          title: 'Employee Code of Conduct',
          category: 'HR',
          description: 'Guidelines for professional behavior and workplace ethics',
          content: 'All employees are expected to maintain the highest standards of professional conduct. This includes:\n\n1. Respect and Dignity: Treat all colleagues, customers, and stakeholders with respect and dignity.\n\n2. Integrity: Act honestly and ethically in all business dealings.\n\n3. Confidentiality: Protect company and customer information.\n\n4. Conflict of Interest: Avoid situations where personal interests conflict with company interests.\n\n5. Workplace Safety: Follow all safety protocols and report hazards immediately.\n\n6. Dress Code: Maintain professional appearance appropriate to your role.\n\n7. Attendance: Be punctual and maintain regular attendance.\n\n8. Communication: Communicate professionally and respectfully in all forms.\n\nViolations of this code may result in disciplinary action up to and including termination.',
          effectiveDate: '2024-01-01',
          lastUpdated: '2024-01-15',
          updatedBy: 'Kalyan',
          version: '2.0',
          status: 'Active',
          tags: ['conduct', 'ethics', 'workplace']
        },
        {
          id: '2',
          title: 'Workplace Safety Policy',
          category: 'Safety',
          description: 'Comprehensive safety guidelines and emergency procedures',
          content: 'Sparsh is committed to providing a safe and healthy work environment for all employees.\n\n1. General Safety:\n- Report all accidents and near-misses immediately\n- Use personal protective equipment (PPE) as required\n- Keep work areas clean and organized\n- Follow all safety signage and warnings\n\n2. Emergency Procedures:\n- Know the location of fire exits and assembly points\n- Participate in all emergency drills\n- Report fire hazards immediately\n- Follow evacuation procedures during emergencies\n\n3. Equipment Safety:\n- Only operate equipment you are trained to use\n- Report damaged or malfunctioning equipment\n- Never bypass safety features\n- Maintain equipment according to schedule\n\n4. Chemical Safety:\n- Read and understand Material Safety Data Sheets (MSDS)\n- Store chemicals properly\n- Use appropriate PPE when handling chemicals\n- Report spills immediately\n\n5. Reporting:\n- All incidents must be reported within 24 hours\n- Safety concerns should be reported to supervisors\n- Anonymous reporting is available',
          effectiveDate: '2024-01-01',
          lastUpdated: '2024-01-10',
          updatedBy: 'Amol',
          version: '3.1',
          status: 'Active',
          tags: ['safety', 'emergency', 'health']
        },
        {
          id: '3',
          title: 'Leave and Attendance Policy',
          category: 'HR',
          description: 'Guidelines for leave requests, attendance, and time off',
          content: 'This policy outlines the leave entitlements and attendance expectations for all employees.\n\n1. Annual Leave:\n- Full-time employees: 21 days per year\n- Part-time employees: Pro-rated based on hours worked\n- Leave must be requested at least 2 weeks in advance\n- Maximum 10 consecutive days without manager approval\n\n2. Sick Leave:\n- 12 days per year for full-time employees\n- Medical certificate required for absences over 2 days\n- Notify supervisor before start of shift\n\n3. Casual Leave:\n- 6 days per year\n- Can be taken with 24 hours notice\n- Subject to operational requirements\n\n4. Public Holidays:\n- All gazetted public holidays are paid leave\n- Work on public holidays requires prior approval\n- Compensatory off will be provided\n\n5. Attendance:\n- Standard working hours: 9:00 AM - 6:00 PM\n- Flexible timing available with manager approval\n- Late arrivals must be reported\n- Excessive absenteeism may result in disciplinary action\n\n6. Leave Application:\n- Submit leave requests through the HR portal\n- Approval required before taking leave\n- Emergency leave must be reported immediately',
          effectiveDate: '2024-01-01',
          lastUpdated: '2024-01-08',
          updatedBy: 'Shirish',
          version: '1.5',
          status: 'Active',
          tags: ['leave', 'attendance', 'time-off']
        },
        {
          id: '4',
          title: 'Data Privacy and Security Policy',
          category: 'IT',
          description: 'Guidelines for handling sensitive data and information security',
          content: 'Sparsh is committed to protecting the privacy and security of all data.\n\n1. Data Classification:\n- Public: Information that can be freely shared\n- Internal: Information for internal use only\n- Confidential: Sensitive business information\n- Restricted: Highly sensitive data requiring special handling\n\n2. Access Control:\n- Use strong passwords (minimum 12 characters)\n- Change passwords every 90 days\n- Never share login credentials\n- Lock computers when away from desk\n- Report lost or stolen devices immediately\n\n3. Data Handling:\n- Encrypt sensitive data in transit and at rest\n- Use secure file sharing methods\n- Shred physical documents containing sensitive information\n- Clear desk policy for confidential materials\n\n4. Email Security:\n- Be cautious of phishing attempts\n- Verify sender identity before clicking links\n- Use encryption for sensitive emails\n- Report suspicious emails to IT\n\n5. Mobile Devices:\n- Enable device encryption\n- Use screen lock with PIN/password\n- Install only approved applications\n- Report lost devices within 2 hours\n\n6. Incident Reporting:\n- Report security incidents immediately\n- Contact IT security team for guidance\n- Document all incidents\n\n7. Compliance:\n- Follow all applicable data protection regulations\n- Complete annual security training\n- Sign confidentiality agreements',
          effectiveDate: '2024-01-01',
          lastUpdated: '2024-01-12',
          updatedBy: 'Kalyan',
          version: '2.2',
          status: 'Active',
          tags: ['data', 'privacy', 'security', 'IT']
        },
        {
          id: '5',
          title: 'Quality Control Standards',
          category: 'Quality',
          description: 'Quality assurance procedures for Moringa products',
          content: 'Sparsh maintains the highest quality standards for all Moringa products.\n\n1. Raw Material Quality:\n- Source only from certified organic farms\n- Inspect all incoming raw materials\n- Test for contaminants and pesticides\n- Reject substandard materials\n\n2. Processing Standards:\n- Follow Good Manufacturing Practices (GMP)\n- Maintain clean and sanitized facilities\n- Regular equipment calibration\n- Temperature and humidity monitoring\n\n3. Product Testing:\n- Test every batch for quality parameters\n- Microbiological testing as per schedule\n- Nutritional analysis verification\n- Heavy metal testing\n- Shelf life studies\n\n4. Documentation:\n- Maintain complete batch records\n- Track all quality test results\n- Document any deviations\n- Keep certificates of analysis\n\n5. Packaging:\n- Use food-grade packaging materials\n- Proper labeling with all required information\n- Tamper-evident seals\n- Batch number and expiry date marking\n\n6. Storage:\n- Maintain proper temperature and humidity\n- First-in-first-out (FIFO) system\n- Regular inventory checks\n- Pest control measures\n\n7. Customer Complaints:\n- Log all complaints within 24 hours\n- Investigate root cause\n- Implement corrective actions\n- Follow up with customers\n\n8. Continuous Improvement:\n- Regular quality audits\n- Staff training programs\n- Process optimization\n- Feedback implementation',
          effectiveDate: '2024-01-01',
          lastUpdated: '2024-01-05',
          updatedBy: 'Amol',
          version: '1.8',
          status: 'Active',
          tags: ['quality', 'standards', 'moringa', 'testing']
        },
        {
          id: '6',
          title: 'Environmental Sustainability Policy',
          category: 'Environmental',
          description: 'Commitment to sustainable and eco-friendly practices',
          content: 'Sparsh is committed to environmental sustainability in all operations.\n\n1. Sustainable Farming:\n- Organic farming practices only\n- No synthetic pesticides or fertilizers\n- Crop rotation and soil conservation\n- Water conservation techniques\n- Biodiversity protection\n\n2. Energy Conservation:\n- Use renewable energy where possible\n- Energy-efficient equipment\n- Regular energy audits\n- Turn off equipment when not in use\n- Natural lighting optimization\n\n3. Waste Management:\n- Reduce, Reuse, Recycle principle\n- Segregate waste at source\n- Composting of organic waste\n- Minimize packaging waste\n- Partner with certified waste handlers\n\n4. Water Management:\n- Rainwater harvesting\n- Drip irrigation systems\n- Water recycling where feasible\n- Regular leak detection and repair\n- Monitor water consumption\n\n5. Carbon Footprint:\n- Measure and track emissions\n- Optimize transportation routes\n- Encourage carpooling\n- Plant trees regularly\n- Carbon offset programs\n\n6. Sustainable Packaging:\n- Use biodegradable materials\n- Minimize packaging\n- Recyclable packaging options\n- Avoid single-use plastics\n\n7. Employee Engagement:\n- Environmental awareness training\n- Green initiatives participation\n- Suggestion programs\n- Recognition for eco-friendly practices\n\n8. Compliance:\n- Follow all environmental regulations\n- Regular environmental audits\n- Transparent reporting\n- Continuous improvement',
          effectiveDate: '2024-01-01',
          lastUpdated: '2024-01-03',
          updatedBy: 'Shirish',
          version: '1.0',
          status: 'Active',
          tags: ['environment', 'sustainability', 'green', 'eco-friendly']
        }
      ];
      setPolicies(initialPolicies);
      localStorage.setItem('sparsh_policies', JSON.stringify(initialPolicies));
    }
  }, []);

  const savePolicies = (updatedPolicies: Policy[]) => {
    setPolicies(updatedPolicies);
    localStorage.setItem('sparsh_policies', JSON.stringify(updatedPolicies));
  };

  const handleAddPolicy = (newPolicy: Omit<Policy, 'id' | 'lastUpdated' | 'updatedBy'>) => {
    const currentUser = localStorage.getItem('userName') || 'User';
    const policy: Policy = {
      ...newPolicy,
      id: Date.now().toString(),
      lastUpdated: new Date().toISOString().split('T')[0],
      updatedBy: currentUser
    };
    const updatedPolicies = [policy, ...policies];
    savePolicies(updatedPolicies);
    setShowAddModal(false);
  };

  const handleEditPolicy = (updatedPolicy: Policy) => {
    const currentUser = localStorage.getItem('userName') || 'User';
    const policy = {
      ...updatedPolicy,
      lastUpdated: new Date().toISOString().split('T')[0],
      updatedBy: currentUser
    };
    const updatedPolicies = policies.map(p => p.id === policy.id ? policy : p);
    savePolicies(updatedPolicies);
    setEditingPolicy(null);
    if (selectedPolicy?.id === policy.id) {
      setSelectedPolicy(policy);
    }
  };

  const handleDeletePolicy = (id: string) => {
    if (confirm('Are you sure you want to delete this policy? This action cannot be undone.')) {
      const updatedPolicies = policies.filter(p => p.id !== id);
      savePolicies(updatedPolicies);
      if (selectedPolicy?.id === id) {
        setSelectedPolicy(null);
      }
    }
  };

  const filteredPolicies = policies.filter(policy => {
    const matchesSearch = policy.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         policy.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         policy.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         policy.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = selectedCategory === 'All' || policy.category === selectedCategory;
    const matchesStatus = selectedStatus === 'All' || policy.status === selectedStatus;
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const stats = {
    total: policies.length,
    active: policies.filter(p => p.status === 'Active').length,
    draft: policies.filter(p => p.status === 'Draft').length,
    archived: policies.filter(p => p.status === 'Archived').length
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col">
        <DashboardHeader />
        <main className="flex-1 p-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Company Policies</h1>
                <p className="text-sm text-gray-600 mt-1">Manage and maintain company policies and procedures</p>
              </div>
              <button
                onClick={() => setShowAddModal(true)}
                className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium flex items-center gap-2 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line text-xl w-5 h-5 flex items-center justify-center"></i>
                Add New Policy
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total Policies</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
                  </div>
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                    <i className="ri-file-list-3-line text-2xl text-teal-600 w-6 h-6 flex items-center justify-center"></i>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Active</p>
                    <p className="text-3xl font-bold text-green-600">{stats.active}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <i className="ri-checkbox-circle-line text-2xl text-green-600 w-6 h-6 flex items-center justify-center"></i>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Draft</p>
                    <p className="text-3xl font-bold text-yellow-600">{stats.draft}</p>
                  </div>
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <i className="ri-draft-line text-2xl text-yellow-600 w-6 h-6 flex items-center justify-center"></i>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Archived</p>
                    <p className="text-3xl font-bold text-gray-600">{stats.archived}</p>
                  </div>
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                    <i className="ri-archive-line text-2xl text-gray-600 w-6 h-6 flex items-center justify-center"></i>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6">
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="flex-1 relative">
                  <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg w-5 h-5 flex items-center justify-center"></i>
                  <input
                    type="text"
                    placeholder="Search policies by title, description, content, or tags..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  />
                </div>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm cursor-pointer"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm cursor-pointer"
                >
                  <option value="All">All Status</option>
                  <option value="Active">Active</option>
                  <option value="Draft">Draft</option>
                  <option value="Archived">Archived</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <PolicyList
                  policies={filteredPolicies}
                  onView={setViewingPolicy}
                  onEdit={setEditingPolicy}
                  onDelete={handleDeletePolicy}
                />
              </div>
              <div className="lg:col-span-1">
                <PolicyViewer 
                  policy={selectedPolicy}
                  onEdit={setEditingPolicy}
                />
              </div>
            </div>
          </div>
        </main>
      </div>

      {showAddModal && (
        <AddPolicyModal
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddPolicy}
          categories={categories.filter(c => c !== 'All')}
        />
      )}

      {editingPolicy && (
        <EditPolicyModal
          policy={editingPolicy}
          onClose={() => setEditingPolicy(null)}
          onSave={handleEditPolicy}
          categories={categories.filter(c => c !== 'All')}
        />
      )}

      {viewingPolicy && (
        <PolicyDetailModal
          policy={viewingPolicy}
          onClose={() => setViewingPolicy(null)}
          onEdit={setEditingPolicy}
        />
      )}
    </div>
  );
}
